import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { TeamDashboardKPIs, TeamMemberWorkload } from '../../../modules/minha-mesa/types/workItem';
import { MAX_ACTIVE_TASKS_PER_OPERATOR, type KpiFilter, isCritical, visibleBottleneck } from './teamCapacityShared';
import { getOccupancyTier } from '../executive/opsExecutiveShared';

interface TeamCapacityKpiCardsProps {
  kpis: TeamDashboardKPIs;
  members: TeamMemberWorkload[];
  activeFilter?: KpiFilter;
  onFilterClick?: (filter: KpiFilter) => void;
  compact?: boolean;
}

export const TeamCapacityKpiCards: React.FC<TeamCapacityKpiCardsProps> = ({
  kpis,
  members,
  activeFilter = null,
  onFilterClick,
  compact = false,
}) => {
  const fmt = (v: number | null) => (v === null ? '--' : v.toFixed(1));
  const totalCapacity = members.length * MAX_ACTIVE_TASKS_PER_OPERATOR;
  const occupationPct = totalCapacity > 0 ? Math.round((kpis.current_workload / totalCapacity) * 100) : 0;
  const occupationBand = getOccupancyTier(occupationPct);
  const criticalCount = members.filter(isCritical).length;
  const slaOkPct = kpis.avg_sla_compliance_pct;
  const slaViolPct = 100 - slaOkPct;

  const topBottleneck = members.length > 0
    ? [...members].sort((a, b) => {
        const rank = (m: TeamMemberWorkload) => {
          const label = visibleBottleneck(m).text;
          return label === 'Alto' ? 2 : label === 'Médio' ? 1 : 0;
        };
        return rank(b) - rank(a) || b.total_active - a.total_active || b.tasks_overdue - a.tasks_overdue;
      })[0]
    : null;
  const hasBottleneck = topBottleneck !== null && visibleBottleneck(topBottleneck).text !== '--' && visibleBottleneck(topBottleneck).text !== 'Baixo';
  const topBottleneckLabel = hasBottleneck ? topBottleneck!.user_name.split(' ')[0] : 'Balanceado';

  const capacidadeColor = occupationBand === 'critical'
    ? 'text-red-600 dark:text-red-400'
    : occupationBand === 'high'
    ? 'text-amber-600 dark:text-amber-400'
    : occupationBand === 'medium'
    ? 'text-sky-600 dark:text-sky-400'
    : 'text-emerald-600 dark:text-emerald-400';

  const cards = [
    {
      key: 'tasks',
      label: 'Tarefas/Operador',
      value: fmt(kpis.tasks_per_employee),
      sub: `${kpis.total_members} operadores`,
      valueColor: 'text-slate-800 dark:text-slate-100',
      labelColor: 'text-[var(--text-muted)]',
      bgClass: 'bg-[var(--bg-surface)]',
    },
    {
      key: 'overdue',
      label: 'Atrasadas',
      value: String(kpis.overdue_tasks),
      sub: kpis.overdue_tasks > 0 ? 'Requer atenção' : 'Nenhuma',
      valueColor: kpis.overdue_tasks > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
      labelColor: kpis.overdue_tasks > 0 ? 'text-red-500 dark:text-red-400' : 'text-[var(--text-muted)]',
      bgClass: kpis.overdue_tasks > 0 ? 'bg-red-50/60 dark:bg-red-500/10' : 'bg-[var(--bg-surface)]',
      clickFilter: 'overdue' as const,
    },
    {
      key: 'capacity',
      label: 'Capacidade',
      value: `${occupationPct}%`,
      sub: `${kpis.current_workload} / ${totalCapacity} tarefas`,
      valueColor: capacidadeColor,
      labelColor: occupationBand === 'critical' || occupationBand === 'high' ? capacidadeColor : 'text-[var(--text-muted)]',
      bgClass: occupationBand === 'critical'
        ? 'bg-red-50/60 dark:bg-red-500/10'
        : occupationBand === 'high'
        ? 'bg-amber-50/60 dark:bg-amber-500/10'
        : 'bg-[var(--bg-surface)]',
      alert: occupationBand === 'critical',
    },
    {
      key: 'bottleneck',
      label: 'Maior ocupação',
      value: topBottleneckLabel,
      sub: hasBottleneck
        ? `${Math.round((topBottleneck!.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100)}% da carga simultânea`
        : 'Sem pressão de carga',
      valueColor: hasBottleneck && visibleBottleneck(topBottleneck!).text === 'Alto'
        ? 'text-red-600 dark:text-red-400'
        : hasBottleneck
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-emerald-600 dark:text-emerald-400',
      labelColor: 'text-[var(--text-muted)]',
      bgClass: hasBottleneck && visibleBottleneck(topBottleneck!).text === 'Alto'
        ? 'bg-red-50/60 dark:bg-red-500/10'
        : hasBottleneck
        ? 'bg-amber-50/60 dark:bg-amber-500/10'
        : 'bg-[var(--bg-surface)]',
    },
    {
      key: 'sla',
      label: 'SLA Equipe',
      value: `${slaOkPct}%`,
      sub: slaViolPct > 0 ? `${slaViolPct}% violado` : 'Conformidade total',
      valueColor: slaOkPct >= 90
        ? 'text-emerald-600 dark:text-emerald-400'
        : slaOkPct >= 70
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400',
      labelColor: 'text-[var(--text-muted)]',
      bgClass: 'bg-[var(--bg-surface)]',
    },
    {
      key: 'critical',
      label: 'Em Risco',
      value: String(criticalCount),
      sub: criticalCount > 0 ? 'carga, prazo ou SLA' : 'Equipe sem esses riscos',
      valueColor: criticalCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
      labelColor: criticalCount > 0 ? 'text-red-500 dark:text-red-400' : 'text-[var(--text-muted)]',
      bgClass: criticalCount > 0 ? 'bg-red-50/60 dark:bg-red-500/10' : 'bg-[var(--bg-surface)]',
      clickFilter: 'critical' as const,
    },
  ];

  const valueSize = compact ? 'text-base' : 'text-xl';

  return (
    <div className={`grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6 ${compact ? 'p-2' : 'py-3'}`}>
      {cards.map((card) => {
        const isClickable = card.clickFilter !== undefined && onFilterClick !== undefined;
        const isActive = isClickable && activeFilter === card.clickFilter;
        return (
          <button
            key={card.key}
            type="button"
            disabled={!isClickable}
            onClick={() => isClickable && onFilterClick!(isActive ? null : card.clickFilter!)}
            className={`${card.bgClass} rounded-sm border text-left transition-all ${
              compact ? 'px-2 py-1.5' : 'px-3 py-2.5'
            } ${
              isActive
                ? 'border-blue-400 ring-1 ring-blue-300/40 dark:border-blue-500/60'
                : 'border-[var(--border-default)]'
            } ${isClickable ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'}`}
          >
            <span className={`text-[8px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100 ${card.labelColor}`}>
              {card.label}
            </span>
            <div className={`mt-0.5 flex items-center gap-1 font-bold tabular-nums ${valueSize} ${card.valueColor}`}>
              {card.alert && <AlertTriangle size={compact ? 11 : 14} className="shrink-0" />}
              {card.value}
            </div>
            {card.sub && <p className={`mt-0.5 truncate text-[8px] ${card.labelColor}`}>{card.sub}</p>}
          </button>
        );
      })}
    </div>
  );
};

export default TeamCapacityKpiCards;
